/**
 * Map backend error messages to translation keys
 * This allows the frontend to display localized error messages
 * instead of raw English messages from the backend
 */

// Map of known backend error messages to translation keys
const ERROR_MAP = {
  // Member/User related errors
  "Email already exists": "members.errors.email_exists",
  "EMAIL ALREADY EXISTS": "members.errors.email_exists",
  "User not found": "members.errors.user_not_found",
  "Cannot delete yourself": "members.errors.cannot_delete_self",
  "Cannot deactivate the licence owner": "members.errors.cannot_deactivate_owner",
  "Cannot delete the licence owner": "members.errors.cannot_delete_owner",
  "Access denied - User not in your licence": "members.errors.user_not_in_licence",
  "Failed to send credentials email. Please verify the email address.": "members.errors.email_send_failed",
  "Invalid email domain": "members.errors.invalid_email_domain",
  
  // Plan/Subscription errors - generic
  "Plan limit reached": "subscription.errors.plan_limit_reached",
  "User limit reached": "subscription.errors.user_limit_reached",
  
  // Plan/Subscription errors - specific limits (from planService.js)
  "Maximum number of users for your license has been reached.": "subscription.errors.users_limit_reached",
  "You have reached the maximum number of profiles allowed by your subscription.": "subscription.errors.profiles_limit_reached",
  "You have reached the maximum number of categories allowed by your subscription.": "subscription.errors.categories_limit_reached",
  "You have reached the monthly limit of transactions for your subscription (one-time this month + recurring).": "subscription.errors.transactions_limit_reached",
  
  // Authentication errors
  "No token provided": "auth.errors.no_token",
  "Invalid token": "auth.errors.invalid_token",
  "Invalid or expired token": "auth.errors.invalid_or_expired_token",
  "Token check failed": "auth.errors.token_check_failed",
  "ACCESS DENIED": "auth.errors.access_denied",
  "Invalid credentials": "login.error.invalid_credentials",
  "Your account has been deactivated. Please contact your licence administrator.": "login.error.user_inactive",
  "Your licence is inactive. Please contact support.": "login.error.licence_inactive",
  
  // Profile access errors
  "Access denied - This action requires license owner privileges": "subscription.errors.license_owner_required",
  "Access denied - No access to this profile": "profile.errors.no_access",
  "Access denied - No read permission": "profile.errors.no_read_access",
  "Access denied - No write permission": "profile.errors.no_write_access",
  "Access denied - Profile not accessible": "profile.errors.no_access",
  "Access denied - User not found": "auth.errors.access_denied",
  "You do not have access to any profile. Please contact your licence administrator.": "no_profile_access.message",
  
  // Password errors
  "Current password is incorrect": "auth.errors.current_password_incorrect",
  "Password changed successfully": "auth.errors.password_changed",
  "Missing required fields": "auth.errors.missing_fields",
  "Token and new password are required": "auth.errors.token_password_required",
  "Password has been reset successfully": "auth.errors.password_reset_success",
  
  // Forgot password / Reset password errors
  "Email is required": "auth.errors.email_required",
  "Token is required": "auth.errors.token_required",
  "If this email exists, a password reset link has been sent": "forgot_password.success.description",
  "An error occurred. Please try again later.": "common.error_try_later",
  "An error occurred. Please try again.": "common.error_try_again",
  "An error occurred": "common.error_generic",
  "Une erreur est survenue": "common.error_generic",
  
  // Email change errors
  "Email must be changed via the secure email-change flow": "email_change.errors.use_secure_flow",
  "New email must be different from current email": "email_change.errors.same_email",
  "This email address is not available": "email_change.errors.email_not_available",
  "Email change is available only for Premium or Ultimate plans.": "email_change.errors.premium_only",
  "Email change is available only for the Premium plan.": "email_change.errors.premium_only",
  "Too many email change requests. Please wait before trying again.": "email_change.errors.too_many_requests",
  "Unable to send verification codes. Please try again later.": "email_change.errors.send_failed",
  "Unable to send verification code. Please try again later.": "email_change.errors.send_failed",
  "Unable to send verification code. Please try again.": "email_change.errors.send_failed",
  "Verification code is required": "email_change.errors.code_required",
  "Both verification codes are required": "email_change.errors.both_codes_required",
  "No pending email change request. Please start over.": "email_change.errors.no_pending_request",
  "No pending email change request": "email_change.errors.no_pending_request",
  "Identity already verified. Proceed to set new email.": "email_change.errors.already_verified",
  "Too many invalid attempts. Please restart the email change process.": "email_change.errors.too_many_invalid",
  "Request expired. Please start again.": "email_change.errors.request_expired",
  "Email change request expired. Please start again.": "email_change.errors.request_expired",
  "Invalid code. Please check and try again.": "email_change.errors.invalid_code",
  "Invalid codes. Please check and try again.": "email_change.errors.invalid_code",
  "Please verify your identity first.": "email_change.errors.verify_identity_first",
  "Please set your new email first.": "email_change.errors.set_new_email_first",
  "Your account email changed during the process. Please restart.": "email_change.errors.email_changed_during",
  "This email is no longer available. Please restart with another address.": "email_change.errors.email_no_longer_available",
  "Unable to complete email change. Please try again.": "email_change.errors.unable_to_complete",
  "Email updated successfully. Please sign in again with your new address.": "email_change.success",
  "Email change cancelled": "email_change.cancelled",
  "newEmail and currentPassword are required": "email_change.errors.fields_required",
  
  // Soft lock / licence errors
  "No active licence found. Please subscribe to continue.": "licence.errors.no_active_licence",
  "Your licence has been suspended. Please contact support or upgrade.": "licence.errors.suspended",
  "Your subscription has expired. Please renew to continue.": "licence.errors.expired",
  
  // Validation errors
  "Invalid email": "validation.errors.invalid_email",
  "Invalid email format": "validation.errors.invalid_email",
  
  // Rate limiter (French messages from backend)
  "Trop de tentatives de connexion": "login.error.too_many_attempts_generic",
  "Trop d'opérations": "common.error_too_many_operations",
  "Trop de requêtes": "common.error_too_many_requests",
  
  // Login history
  "Failed to fetch login history": "auth.errors.login_history_failed",
  "At least one login ID is required": "auth.errors.login_id_required",
  "No valid login entries found": "auth.errors.no_valid_login_entries",
  
  // Security reports
  "Report submitted successfully. Our team will investigate.": "auth.errors.report_submitted",
  "Failed to submit report": "auth.errors.report_failed",
  
  // Generic errors
  "Server error": "common.server_error",
  "Error checking profile access": "common.server_error",
  "Error checking privileges": "common.server_error",
};

/**
 * Get the translation key for a backend error message
 * @param {string} backendMessage - The error message from the backend
 * @param {string} fallbackKey - The fallback translation key if no mapping found
 * @returns {string} - The translation key to use
 */
export const getErrorTranslationKey = (backendMessage, fallbackKey = "common.server_error") => {
  if (!backendMessage) return fallbackKey;
  
  // Check for exact match
  if (ERROR_MAP[backendMessage]) {
    return ERROR_MAP[backendMessage];
  }
  
  // Check for partial matches (case insensitive)
  const lowerMessage = backendMessage.toLowerCase();
  for (const [key, value] of Object.entries(ERROR_MAP)) {
    if (lowerMessage.includes(key.toLowerCase())) {
      return value;
    }
  }
  
  return fallbackKey;
};

/**
 * Translate a backend error message using the provided translation function
 * @param {string} backendMessage - The error message from the backend
 * @param {Function} t - The translation function from i18next
 * @param {string} fallbackKey - The fallback translation key if no mapping found
 * @returns {string} - The translated error message
 */
export const translateBackendError = (backendMessage, t, fallbackKey = "common.server_error") => {
  const translationKey = getErrorTranslationKey(backendMessage, fallbackKey);
  return t(translationKey);
};

export default {
  getErrorTranslationKey,
  translateBackendError,
};
